import React, { useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

interface CopyToClipboardProps {
  text: string;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  /** Replaces the default ContentCopy icon (Check still shows after copy). */
  icon?: React.ReactNode;
  sx?: React.ComponentProps<typeof IconButton>['sx'];
  tooltipTitle?: string;
  'aria-label'?: string;
}

const CopyToClipboard: React.FC<CopyToClipboardProps> = ({
  text,
  size = 'small',
  disabled = false,
  icon,
  sx,
  tooltipTitle = 'Copy to clipboard',
  'aria-label': ariaLabel = 'copy to clipboard',
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (event?: React.MouseEvent) => {
    event?.stopPropagation();
    if (disabled) {
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const getIconSize = () => {
    switch (size) {
      case 'small':
        return { fontSize: '0.8rem' };
      case 'medium':
        return { fontSize: '1rem' };
      case 'large':
        return { fontSize: '1.5rem' };
      default:
        return { fontSize: '1rem' };
    }
  };

  const defaultCopyIcon = <ContentCopyIcon sx={getIconSize()} />;
  const copyIcon = icon ?? defaultCopyIcon;

  return (
    <Tooltip title={disabled ? 'No value to copy' : copied ? 'Copied!' : tooltipTitle}>
      <IconButton
        onClick={handleCopy}
        disabled={disabled}
        size={size}
        aria-label={ariaLabel}
        sx={[
          {
            color: disabled ? 'action.disabled' : copied ? 'success.main' : 'inherit',
            p: 0.5,
            ml: 0.5,
          },
          ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
        ]}
      >
        {copied ? <CheckIcon sx={getIconSize()} /> : copyIcon}
      </IconButton>
    </Tooltip>
  );
};

export default CopyToClipboard;
